var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useEffect, useRef, useState } from 'react';
import 'keen-slider/keen-slider.min.css';
import { useKeenSlider } from 'keen-slider/react';
import useResizeObserver from '@react-hook/resize-observer';
import { renderSlides } from './utils';
import Navigator from './Navigator';
import Dots from './Dots';
import Arrows from './Arrows';
import { CarouselContainer } from './CarouselStyles';
/** [Pigment Carousel](https://pigment.customink.com/?path=/docs/components-carousel--page)
 */
var Carousel = function (_a) {
    var _b = _a.arrows, arrows = _b === void 0 ? true : _b, _c = _a.arrowPosition, arrowPosition = _c === void 0 ? 'inside' : _c, _d = _a.arrowOffset, arrowOffset = _d === void 0 ? 40 : _d, _e = _a.dots, dots = _e === void 0 ? true : _e, _f = _a.lazy, lazy = _f === void 0 ? false : _f, _g = _a.lazyOffset, lazyOffset = _g === void 0 ? 1 : _g, _h = _a.loop, loop = _h === void 0 ? true : _h, _j = _a.navigatorSide, navigatorSide = _j === void 0 ? 'left' : _j, _k = _a.sliderBg, sliderBg = _k === void 0 ? 'white' : _k, _l = _a.sliderHeight, sliderHeight = _l === void 0 ? '350px' : _l, sliderStyle = _a.sliderStyle, slides = _a.slides, _m = _a.slidesPerView, slidesPerView = _m === void 0 ? 1 : _m, _o = _a.slidesSpacing, slidesSpacing = _o === void 0 ? 0 : _o, sx = _a.sx, thumbnails = _a.thumbnails, _p = _a.thumbSize, thumbSize = _p === void 0 ? 'medium' : _p, onCreated = _a.onCreated, onPreviousClick = _a.onPreviousClick, onNextClick = _a.onNextClick, onSlideClick = _a.onSlideClick, props = __rest(_a, ["arrows", "arrowPosition", "arrowOffset", "dots", "lazy", "lazyOffset", "loop", "navigatorSide", "sliderBg", "sliderHeight", "sliderStyle", "slides", "slidesPerView", "slidesSpacing", "sx", "thumbnails", "thumbSize", "onCreated", "onPreviousClick", "onNextClick", "onSlideClick"]);
    var _q = useState([]), loaded = _q[0], setLoaded = _q[1];
    var _r = useState(0), currentSlide = _r[0], setCurrentSlide = _r[1];
    var _s = useKeenSlider({
        initial: 0,
        loop: loop,
        slides: {
            origin: slidesPerView > 1 ? 'auto' : 'center',
            perView: slidesPerView,
            spacing: slidesSpacing,
        },
        animationEnded: function (slider) {
            setCurrentSlide(slider.track.details.rel);
        },
        slideChanged: function (slider) {
            setCurrentSlide(slider.track.details.rel);
        },
        created: onCreated,
    }), keenSliderCallbackRef = _s[0], instanceRef = _s[1];
    /*
      Resize keenSlider when the bounding div is resized. KeenSlider automatically listens for window resize, but we have to do it manually when layout is changed, and Carousel re-rendered.
      Not to be confused, `keenSliderCallbackRef` and `boundingBoxRef` are handled very differently by React:
        `keenSliderCallbackRef` is a  _callback ref_: it is just a method provided by keen-slider, to be passed as ref to the div. It is not a RefObject, doesn't have `.current`
        `boundingBoxRef` is a RefObject, and as such, it will hold the div element in `.current` when passed as ref, which is what useResizeObserver needs
        More details on callback ref: https://react.dev/reference/react-dom/components/common#ref-callback
    */
    var boundingBoxRef = useRef(null);
    useResizeObserver(boundingBoxRef, function () { var _a; return (_a = instanceRef.current) === null || _a === void 0 ? void 0 : _a.update(); });
    useEffect(function () {
        var new_loaded = __spreadArray([], loaded, true);
        new_loaded[currentSlide] = true;
        // should set immediate neighbors of current slide, so for lazyOffset === 1 and currentSlide === 0,
        // [<current slide (loaded)>, <set true>, false, false, <set true>]
        for (var i = 1; i < slidesPerView + lazyOffset; i++) {
            new_loaded[(currentSlide - i + loaded.length) % loaded.length] = true;
            new_loaded[(currentSlide + i + loaded.length) % loaded.length] = true;
        }
        setLoaded(new_loaded);
    }, [currentSlide]);
    var hasThumbs = !!(thumbnails === null || thumbnails === void 0 ? void 0 : thumbnails.length);
    var showArrows = slides.length >= 2 && arrows;
    var cssArrowOffset = typeof arrowOffset === 'number' ? "".concat(arrowOffset, "px") : arrowOffset;
    return (React.createElement(CarouselContainer, __assign({ hasThumbs: !!(thumbnails === null || thumbnails === void 0 ? void 0 : thumbnails.length), thumbSize: thumbSize, navigatorSide: navigatorSide, sliderHeight: sliderHeight, sliderBg: sliderBg, sx: sx, arrowPosition: arrowPosition, arrowOffset: cssArrowOffset }, props),
        React.createElement("div", { className: "slider", ref: boundingBoxRef, style: sliderStyle },
            showArrows ? (React.createElement(Arrows, { instanceRef: instanceRef, loop: loop, currentSlide: currentSlide, position: arrowPosition, offset: cssArrowOffset, onPreviousClick: onPreviousClick, onNextClick: onNextClick })) : null,
            React.createElement("div", { ref: keenSliderCallbackRef, className: "keen-slider", onClick: onSlideClick }, renderSlides({ lazy: lazy, loaded: loaded, slides: slides })),
            !hasThumbs && dots && slides.length > 1 ? (React.createElement(Dots, { number: slides.length, currentSlide: currentSlide, instanceRef: instanceRef })) : null),
        hasThumbs && instanceRef.current ? (React.createElement(Navigator, { currentSlide: currentSlide, thumbnails: thumbnails, thumbSize: thumbSize, navigatorSide: navigatorSide, instanceRef: instanceRef })) : null));
};
export default Carousel;
//# sourceMappingURL=Carousel.js.map